import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
    TextField,
    Modal,
    Box,
    CircularProgress,
    Select,
    MenuItem,
} from "@mui/material";
import cogoToast from "cogo-toast";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import bg0 from "../assets/images/leadMagnet/bg0.svg";
import bg1 from "../assets/images/leadMagnet/bg1.svg";
import bg2 from "../assets/images/leadMagnet/bg2.svg";
import blueBack from "../assets/images/leadMagnet/blue-back.png";
import {
    getLeadMagnet,
    postLeadMagnetResponseForm,
    downloadLeadMagnet,
} from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import { images } from "../constants/images";
import { Helmet } from "react-helmet-async";

// Modal styles
const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "500px" },
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
    outline: "none",
};

// Hero Section Component
const HeroSection = ({
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
    offeringType,
}) => (
    <section
        className="page-hero-sec"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
        }}
    >
        <div className="container mb-5">
            <div className="row align-items-center">
                <div className="col-lg-7 col-md-12">
                    <div className="hero-content text-white">
                        <h1
                            className="head-font mb-4"
                            style={{
                                fontSize: "3.5rem",
                                fontWeight: "bold",
                                lineHeight: "1.2",
                            }}
                        >
                            How to Audit Your Agency’s Proposal Before Signing
                        </h1>
                        <p
                            className="fs-17 mb-4 text-warning"
                            style={{ lineHeight: "1.6", fontWeight: "bold" }}
                        >
                            Before you sign that software proposal wait. Here’s how to spot hidden risks, missing details, and overpriced fluff, before it’s too late.

                        </p>
                        <p
                            className="fs-17 mb-4 text-white"
                            style={{ lineHeight: "1.6" }}
                        >
                            Every week, founders tell us the same story: “The proposal looked great until the problems began.”
                            This free eBook helps you read a tech proposal like a pro, even if you don’t speak a word of “developer.”
                        </p>

                    </div>
                </div>
                <div className="col-lg-5 col-md-12">
                    <div
                        className="hero-form bg-white p-4 rounded shadow-lg"
                        style={{ maxWidth: "400px", margin: "0 auto" }}
                    >
                        <div className="text-center mb-4">
                            <h5 className="tc-primary head-font mb-2">
                                Get Your Free Guide
                            </h5>
                            <p className="text-muted small">
                                Audit any proposal in 3 minutes
                            </p>
                        </div>

                        
                        {(!isFormSubmitted ||
                            (offeringType === "E-Book")) && (
                                <>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            error={showError && !formData.name.trim()}
                                            helperText={
                                                showError && !formData.name.trim()
                                                    ? "Name is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={showError && !formData.email.trim()}
                                            helperText={
                                                showError && !formData.email.trim()
                                                    ? "Valid email is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phone"
                                            type="text"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            error={showError && !formData.phone.trim()}
                                            helperText={
                                                showError && !formData.phone.trim()
                                                    ? "Valid phone is required"
                                                    : ""
                                            }
                                            inputProps={{ maxLength: 10 }}
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <Select
                                            fullWidth
                                            name="nicheType"
                                            value={formData.nicheType}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            error={showError && !formData.nicheType}
                                            sx={{
                                                "& .MuiSelect-select": {
                                                    background: "#e0e5ef",
                                                    padding: "12px 14px",
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Industry Type
                                            </MenuItem>
                                            {nicheTypes?.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {showError && !formData.nicheType && (
                                            <p
                                                style={{
                                                    color: "#d32f2f",
                                                    fontSize: "0.75rem",
                                                    margin: "3px 14px 0",
                                                }}
                                            >
                                                Industry type is required
                                            </p>
                                        )}
                                    </div>

                                    {formData.nicheType === "Other" && (
                                        <div className="mb-4">
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="Specify Your Industry"
                                                name="industry_other"
                                                value={formData.industry_other}
                                                onChange={handleInputChange}
                                                error={
                                                    showError &&
                                                    formData.nicheType === "Other" &&
                                                    !formData.industry_other.trim()
                                                }
                                                helperText={
                                                    showError &&
                                                        formData.nicheType === "Other" &&
                                                        !formData.industry_other.trim()
                                                        ? "Please specify your industry"
                                                        : ""
                                                }
                                                required={formData.nicheType === "Other"}
                                                margin="normal"
                                                inputProps={{ maxLength: 100 }}
                                            />
                                        </div>
                                    )}

                                    <button
                                        onClick={submitLeadForm}
                                        className="LM_link_btn1"
                                        disabled={isSubmitting}
                                        style={{
                                            background: "#0a2458",
                                            border: "none",
                                            padding: "12px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ color: "white", mr: 1 }}
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Get Your Free E-Book"
                                        )}
                                    </button>

                                    <p className="text-center text-muted small mt-2 mb-0">
                                        No spam. No fluff. Just clarity.
                                    </p>
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const WhatsInside = ({ openModal }) => {
    return (
        <section
            className="d-flex align-items-center justify-content-center min-vauto py-5"
            style={{
                backgroundImage: `url(${bg0})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="container mt-5">
                <div className="row text-center mb-5 justify-content-center">
                    <div className="col-lg-10">
                        <h2 className="tc-primary text-center head-font mb-4">
                            What's Inside (And Why You'll Love It)
                        </h2>

                        <ul
                            className="text-start"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontWeight: 400,
                                fontSize: "20px",
                                lineHeight: "36px",
                                color: "#000000",
                                maxWidth: "800px",
                                margin: "0 auto",
                            }}
                        >
                            <li>✅ The 6 must-have sections every legit proposal should include.</li>
                            <li>✅ Real red flags that 80% of agencies try to hide.</li>
                            <li>✅ A 3-minute self-audit checklist to test if your quote is solid or sketchy.</li>
                            <li>✅ How to compare two proposals beyond "who's cheaper."</li>
                            <li>✅ Pro-level questions that instantly reveal if an agency is bluffing.</li>
                        </ul>

                        <button onClick={openModal} className="LM_link_btn mt-4">
                            Get Your Free E-Book
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

const WhoIsThisFor = ({ openModal }) => (
    <section
        className="d-flex align-items-center justify-content-center min-vauto"
        style={{
            backgroundImage: `url(${bg1})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container mt-1 mb-5">
            <div className="row justify-content-center mt-5">
                <div className="col-lg-6">
                    <img
                        src={images.ebook5}
                        alt="Proposal Audit Guide"
                        className="top-0 start-0 w-100 auto"
                        style={{ objectFit: "cover" }}
                    />
                </div>
                <div className="col-lg-6 align-self-center">
                    <h2 className="tc-primary text-center head-font mb-4">
                        Who This Is Perfect For
                    </h2>

                    <ul
                        className="text-center"
                        style={{
                            listStyle: "none",
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: "22px",
                            lineHeight: "36px",
                            color: "#000000",
                            padding: "2rem 0",
                        }}
                    >
                        <li style={{ fontSize: 20, marginBottom: "1rem" }}>
                            👉 Founders about to sign a development proposal.
                        </li>
                        <li style={{ fontSize: 20, marginBottom: "1rem" }}>
                            👉 Non-tech business owners tired of hearing "trust us, we'll handle it."
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 Anyone who's been burned before and refuses to let it happen again.
                        </li>
                    </ul>

                    <div className="text-center">
                        <button onClick={openModal} className="LM_link_btn">
                            Get Your Free E-Book
                        </button>
                    </div>
                    <br /> <br />
                </div>
            </div>
        </div>
    </section>
);

const WhyItsDifferent = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <h3 className="tc-primary head-font mb-4 text-center text-white">
                Why This Guide Feels Different
            </h3>

            <div className="text-center text-white">
                <p
                    className="mt-5"
                    style={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "24px",
                        lineHeight: "40px",
                        maxWidth: "850px",
                        margin: "2rem auto",
                    }}
                >
                    Because it's not written by marketers — it's written by people who audit software proposals every day.
                </p>
                <p
                    style={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "22px",
                        lineHeight: "36px",
                        maxWidth: "850px",
                        margin: "0 auto 1rem",
                    }}
                >
                    We've seen the red flags, decoded the jargon, and built a guide that helps you protect your time, money, and peace of mind.
                </p>
                <p
                    style={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "22px",
                        lineHeight: "36px",
                        maxWidth: "850px",
                        margin: "0 auto",
                        fontStyle: "italic",
                    }}
                >
                    You'll laugh, you'll nod, and by the end — you'll never look at a proposal the same way again.
                </p>
            </div>

            <div className="text-center mt-5">
                <button onClick={openModal} className="LM_link_btn_white w-auto">
                    Get Your Free E-Book
                </button>
            </div>
        </div>
    </section>
);

const WhatYouWillWalkAway = ({ openModal }) => (
    <section
        className="pt-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center mb-5">
                <h2 className="tc-primary head-font mb-4 text-center text-white">What You'll Walk Away With</h2>

                <div className="text-center text-white">
                    <p
                        className="mt-5"
                        style={{
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "24px",
                            lineHeight: "36px",
                            color: "#000000",
                        }}
                    >
                        <ul
                            className="text-white"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "24px",
                                lineHeight: "36px",
                                color: "#000000",
                            }}
                        >
                            <li style={{ fontSize: 20 }}>
                                👉 Confidence to challenge vague terms.
                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 Clarity on what should (and shouldn't) be in your quote.
                            </li>
                            <li style={{ fontSize: 20 }}>
                                👉 A simple system to validate any proposal before signing.
                            </li>
                        </ul>
                        <br />
                    </p>
                </div>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white w-auto">
                        Get Your Free E-Book
                    </button>
                </div>

                <br />

            </div>
        </div>
    </section>
);



const BonusSection = ({ openModal }) => (
    <section className="py-5" style={{ backgroundColor: "#f8fafc" }}>
        <div className="container">
            <div className="text-center">
                <h2 className="tc-primary head-font mb-4">Bonus: We'll Review Your Proposal (For Free)</h2>
                <p className="fs-20 text-muted mb-4" style={{ maxWidth: "800px", margin: "0 auto 2rem" }}>
                    Once you've read it, you'll also get an invite to our <strong>Free Software Audit Review</strong> — where our architects will review your proposal personally and tell you:
                </p>
                <ul
                    className="text-center"
                    style={{
                        listStyle: "none",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "#333",
                        maxWidth: "600px",
                        margin: "0 auto 2rem",
                    }}
                >
                    <li>👉 What's missing</li>
                    <li>👉 What's overpriced</li>
                    <li>👉 What's perfectly fine</li>
                </ul>
                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                    So you can sign (or walk away) with confidence.
                </p>
                <button onClick={openModal} className="LM_link_btn">
                    Get Your Free E-Book
                </button>
            </div>
        </div>
    </section>
);

const TrustSection = () => (
    <section className="py-5" style={{ backgroundColor: "#fff" }}>
        <div className="container">
            <div className="row text-center justify-content-center">
                <div className="col-lg-10">
                    <div className="row g-6">
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    ✅ Used by 300+ founders
                                </p>

                                <p className="text-muted mb-0">to vet software proposals</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    🧩 Built by senior architects
                                </p>
                                <p className="text-muted mb-0">not random bloggers</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>
                                    🔐 100% privacy
                                </p>
                                <p className="text-muted mb-0">We hate spam too</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </section>
);


const TestimonialSection = () => (
    <section
        className="py-75"
        style={{ backgroundColor: "#fff" }}
    >
        <div className="container">
            <div
                className="row justify-content-start"
                style={{ paddingTop: "50px", paddingLeft: "30px" }}
            >
                <div className="col-lg-10">
                    <div className="text-start mb-5">


                        <h3 className="tc-primary head-font mb-4" style={{
                            fontWeight: "bold",
                            fontStyle: "italic",

                        }}>
                            "I almost signed a ₹7L deal — this guide made me spot what was missing!"
                        </h3>
                        <p
                            className="fs-20 mb-4"
                            style={{ maxWidth: "1045px", margin: "0 auto 2rem" }}
                        >
                            — Rajat, SaaS Founder
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);





const FinalCtaSection = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center text-white">
                <h2 className="tc-primary head-font mb-4 text-center text-white">
                    Ready to Audit Before You Sign?
                </h2>
                <p className="fs-20 mb-4 text-center text-white" style={{ maxWidth: "750px", margin: "0 auto 2rem" }}>
                    Drop your email below and we'll send the guide instantly. No spam. No fluff. Just a clear, founder-friendly PDF that'll save you from a world of project pain.
                </p>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white">
                        Get Your Free E-Book
                    </button>
                </div>
            </div>
        </div>
    </section>
);




// Lead Capture Modal Component
const LeadCaptureModal = ({
    isOpen,
    closeModal,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    offeringType,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
}) => (
    <Modal
        open={isOpen}
        onClose={closeModal}
        aria-labelledby="lead-capture-modal"
        aria-describedby="lead-capture-form"
    >
        <Box sx={modalStyle}>
            <div className="text-center mb-4">
                <h4 className="tc-primary head-font">
                    Get Your Free How to Audit Your Agency’s Proposal Before Signing E-Book
                </h4>
                <p className="text-muted">
                    Enter your details below and we'll send the guide directly to your inbox.
                </p>
            </div>

            {(!isFormSubmitted || (offeringType === "E-Book")) && (
                <>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={showError && !formData.name.trim()}
                            helperText={
                                showError && !formData.name.trim() ? "Name is required" : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 100 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={showError && !formData.email.trim()}
                            helperText={
                                showError && !formData.email.trim()
                                    ? "Valid email is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 150 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            type="text"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={showError && !formData.phone.trim()}
                            helperText={
                                showError && !formData.phone.trim()
                                    ? "Valid phone is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 10 }}
                        />
                    </div>

                    <div className="mb-4">
                        <Select
                            fullWidth
                            name="nicheType"
                            value={formData.nicheType}
                            onChange={handleInputChange}
                            displayEmpty
                            error={showError && !formData.nicheType}
                            sx={{
                                "& .MuiSelect-select": {
                                    background: "#e0e5ef",
                                    padding: "12px 14px",
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                Industry Type
                            </MenuItem>
                            {nicheTypes?.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>

                        {showError && !formData.nicheType && (
                            <p
                                style={{
                                    color: "#d32f2f",
                                    fontSize: "0.75rem",
                                    margin: "3px 14px 0",
                                }}
                            >
                                Industry type is required
                            </p>
                        )}
                    </div>

                    {formData.nicheType === "Other" && (
                        <div className="mb-4">
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Specify Your Industry"
                                name="industry_other"
                                value={formData.industry_other}
                                onChange={handleInputChange}
                                error={
                                    showError &&
                                    formData.nicheType === "Other" &&
                                    !formData.industry_other.trim()
                                }
                                helperText={
                                    showError &&
                                        formData.nicheType === "Other" &&
                                        !formData.industry_other.trim()
                                        ? "Please specify your industry"
                                        : ""
                                }
                                required={formData.nicheType === "Other"}
                                margin="normal"
                                inputProps={{ maxLength: 100 }}
                            />
                        </div>
                    )}
                    <div className="d-flex gap-2">
                        <button
                            onClick={closeModal}
                            className="btn btn-outline-secondary flex-fill"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submitLeadForm}
                            className="LM_link_btn1"
                            disabled={isSubmitting}
                            // style={{
                            //     background: "#1e3a8a",
                            //     border: "none",
                            // }}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                                    Sending...
                                </>
                            ) : (
                                "Get Your Free E-Book"
                            )}
                        </button>
                    </div>
                    <p className="text-center text-muted small mt-3 mb-0">
                        No spam. No fluff. Just clarity.
                    </p>
                </>
            )}
        </Box>
    </Modal>
);

const AuditProposalGuideLeadMagnet = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        nicheType: "",
        industry_other: "",
    });
    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadLink, setDownloadLink] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const { slug: paramSlug } = useParams();
    const location = useLocation();

    const nicheTypes = [
        "Manufacturing & Industrial",
        "Technology & Software",
        "Professional Services",
        "Retail & E-commerce",
        "Construction & Real Estate",
        "Logistics & Supply Chain",
        "Energy & Utilities",
        "Healthcare & Life Sciences",
        "Education & Nonprofit",
        "Finance & Insurance",
        "Media, Entertainment & Hospitality",
        "Other",
    ];


    const fallbackSlugMap = {
        "/lm/how-to-audit-your-agencys-proposal-before-signing": "how-to-audit-your-agencys-proposal-before-signing",
    };

    const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

    const searchParams = new URLSearchParams(location.search);
    const utmParams = {
        utm_source: searchParams.get("utm_source") || "direct",
        utm_medium: searchParams.get("utm_medium") || "email",
        utm_campaign: searchParams.get("utm_campaign") || "default",
    };

    const {
        apiData: leadMagnet,
        isLoading: isLeadMagnetLoding,
        error: leadMagnetError,
    } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

    useEffect(() => {
        if (leadMagnetError) {
            cogoToast.error("Failed to load content");
        }
        if (leadMagnet?.status === "error") {
            cogoToast.error("Failed to load content");
        }
    }, [leadMagnetError, leadMagnet]);

    useEffect(() => {
        document.title = "Audit Your Proposal - Plural Code Technologies";
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const openModal = () => setIsModalOpen(true);

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            email: "",
            phone: "",
            nicheType: "",
            industry_other: "",
        });
        setShowError(false);
    };

    const submitLeadForm = async () => {
        const { name, email, phone, nicheType, industry_other } = formData;

        if (!name.trim() || !email.trim() || !phone.trim() || !nicheType) {
            setShowError(true);
            cogoToast.info("Please fill in all required fields");
            return;
        }

        if (nicheType === "Other" && !industry_other.trim()) {
            setShowError(true);
            cogoToast.info("Please specify your industry");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setShowError(true);
            cogoToast.error("Please enter a valid email address");
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(phone)) {
            setShowError(true);
            cogoToast.error("Phone number must be exactly 10 digits");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name,
                email,
                phone,
                slug: leadMagnet?.slug,
                nicheType,
                industry_other: nicheType === "Other" ? industry_other : "",
                offering_type: leadMagnet?.offering_type,
            };

            const response = await postLeadMagnetResponseForm(payload);

            if (response.status === "ok") {
                cogoToast.success(
                    "Thank you! Your E-Book will be sent to your email shortly."
                );
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    nicheType: "",
                    industry_other: "",
                });
                setShowError(false);
                setIsFormSubmitted(true);
            } else {
                cogoToast.error(
                    response.message || "Something went wrong. Please try again."
                );
            }
        } catch (err) {
            console.error("Form submission error:", err);
            cogoToast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManualDownload = async () => {
        if (!downloadLink) {
            cogoToast.error("No download token available");
            return;
        }

        try {
            const downloadResult = await downloadLeadMagnet(actualSlug, downloadLink);

            if (downloadResult.success) {
                const link = document.createElement("a");
                link.href = downloadResult.fileUrl;
                link.download = downloadResult.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                cogoToast.success("Download started! Check your Downloads folder.");
                setDownloadLink("");

                setTimeout(() => {
                    window.URL.revokeObjectURL(downloadResult.fileUrl);
                }, 30000);
            } else {
                cogoToast.error(
                    downloadResult.error || "Download failed. Please contact our team."
                );
                setDownloadLink("");
            }
        } catch (error) {
            console.error("Download error:", error);
            cogoToast.error("Download failed. Please try again.");
        }
    };

    if (isLeadMagnetLoding) {
        return (
            <div
                className="container text-center"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    if (leadMagnetError) {
        return (
            <div className="container text-center py-5">
                <h3>Error loading the page</h3>
                <p>{leadMagnetError.message || "Unknown error occurred"}</p>
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <meta name="title" content={leadMagnet?.meta_title || "Audit Your Proposal - Plural Code Technologies"} />
                <meta name="description" content={leadMagnet?.meta_description || "Learn how to audit software proposals before signing"} />
                <meta
                    property="og:description"
                    content={leadMagnet?.meta_description || "Learn how to audit software proposals before signing"}
                />
            </Helmet>
            <main className="wrapper">
                <HeroSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    nicheTypes={nicheTypes}
                    offeringType={leadMagnet?.offering_type}
                />

                <WhatsInside openModal={openModal} />

                <WhyItsDifferent openModal={openModal} />

                <WhoIsThisFor openModal={openModal} />

                <WhatYouWillWalkAway openModal={openModal} />

                <BonusSection openModal={openModal} />

                <TrustSection />

                <FinalCtaSection openModal={openModal} />

                <TestimonialSection />

                <LeadCaptureModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    offeringType={leadMagnet?.offering_type}
                    nicheTypes={nicheTypes}
                />
            </main>
        </div>
    );
};

export default AuditProposalGuideLeadMagnet;